import type {
  BillingInterval,
  CommercialTermsProductVariantInput,
  FixedProductVariantInput,
  PaymentTerm,
  PlanInputFormFixedProduct,
  PlanInputFormUsageProduct,
  UsageProductVariantInput,
} from '@vy/types';
import {
  ADVANCE_PAYMENT_TYPES,
  BILLING_INTERVALS,
  FIXED_PRODUCT_TYPES,
  MAX_TO,
  PAYMENT_TERMS,
  PRICING_MODELS,
} from '@vy/types';
import { newId, validateNotNullOrUndefined } from '@vy/utils';

const DEFAULT_INFINITY_VALUE = MAX_TO;

export type ProductBuilderInputs = {
  paymentTerm?: PaymentTerm;
  billingInterval?: BillingInterval;
  proRata?: boolean;
  chargingCadence?: number;
  tierLock?: boolean;
  isCalendarAligned?: boolean;
};

export const getDefaultFixedProduct = ({
  paymentTerm,
  billingInterval,
}: ProductBuilderInputs): PlanInputFormFixedProduct => {
  validateNotNullOrUndefined(paymentTerm, 'paymentTerm');
  validateNotNullOrUndefined(billingInterval, 'billingInterval');

  return {
    id: newId(),
    variants: [getDefaultFixedProductVariant()],
    paymentTerm,
    billingInterval,
    isCalendarAligned: false,
  };
};

export const getDefaultCommissionProduct = (): PlanInputFormFixedProduct => ({
  id: newId(),
  variants: [getDefaultCommissionProductVariant()],
  paymentTerm: PAYMENT_TERMS.Prepayment,
  billingInterval: BILLING_INTERVALS.Monthly,
  isCalendarAligned: false,
});

export const getDefaultSubscriptionProduct = ({
  proRata,
  chargingCadence,
  billingInterval,
  paymentTerm,
  isCalendarAligned,
}: ProductBuilderInputs): PlanInputFormFixedProduct => {
  validateNotNullOrUndefined(paymentTerm, 'paymentTerm');
  validateNotNullOrUndefined(billingInterval, 'billingInterval');

  return {
    id: newId(),
    variants: [getDefaultSubscriptionProductVariant()],
    paymentTerm,
    billingInterval,
    proRata,
    chargingCadence,
    isCalendarAligned: isCalendarAligned ?? true,
  };
};

export const getDefaultCommercialTermsProduct = ({
  paymentTerm,
  billingInterval,
}: ProductBuilderInputs): PlanInputFormFixedProduct => {
  validateNotNullOrUndefined(paymentTerm, 'paymentTerm');
  validateNotNullOrUndefined(billingInterval, 'billingInterval');

  return {
    id: newId(),
    variants: [getDefaultCommercialTermsProductVariant()],
    proRata: false,
    paymentTerm,
    billingInterval,
    isCalendarAligned: false,
  };
};

export const getDefaultRefundProduct = (): PlanInputFormFixedProduct => ({
  id: newId(),
  variants: [getDefaultRefundProductVariant()],
  paymentTerm: PAYMENT_TERMS.Prepayment,
  billingInterval: BILLING_INTERVALS.Monthly,
  isCalendarAligned: false,
});

export const getDefaultTiersUsageProduct = (): PlanInputFormUsageProduct => ({
  id: newId(),
  variants: [getDefaultTiersUsageProductVariant()],
  paymentTerm: PAYMENT_TERMS.Postpayment,
  billingInterval: BILLING_INTERVALS.Monthly,
  proRata: true,
  isCalendarAligned: true,
});

export const getDefaultTieredSubscriptionUsageProduct = (tierLock = true): PlanInputFormUsageProduct => ({
  id: newId(),
  variants: [getDefaultTieredSubscriptionUsageProductVariant(tierLock)],
  paymentTerm: PAYMENT_TERMS.Prepayment,
  advancePayment: {
    advancePaymentType: ADVANCE_PAYMENT_TYPES.Tiers,
    advancePaymentConfig: {
      separateLineItems: true,
      payInAdvanceOnTierCross: false,
    },
  },
  billingInterval: BILLING_INTERVALS.Monthly,
  accumulationInterval: 1,
  chargingCadence: 1,
  proRata: true,
  isCalendarAligned: true,
});

export const getDefaultRevShareUsageProduct = (): PlanInputFormUsageProduct => ({
  id: newId(),
  isRevShare: true,
  variants: [getDefaultRevShareProductVariant()],
  paymentTerm: PAYMENT_TERMS.Postpayment,
  billingInterval: BILLING_INTERVALS.Monthly,
  accumulationInterval: 1,
  proRata: true,
  isCalendarAligned: true,
});

export const getDefaultVolumeUsageProduct = (): PlanInputFormUsageProduct => ({
  id: newId(),
  variants: [getDefaultVolumeUsageProductVariant()],
  paymentTerm: PAYMENT_TERMS.Postpayment,
  billingInterval: BILLING_INTERVALS.Monthly,
  proRata: true,
  isCalendarAligned: true,
});

export const getDefaultPerUnitUsageProduct = (): PlanInputFormUsageProduct => ({
  id: newId(),
  variants: [getDefaultPerUnitUsageProductVariant()],
  paymentTerm: PAYMENT_TERMS.Postpayment,
  billingInterval: BILLING_INTERVALS.Monthly,
  proRata: true,
  isCalendarAligned: true,
});

export const getDefaultCreditPromotion = (): PlanInputFormFixedProduct => ({
  id: newId(),
  variants: [getDefaultFixedDeductionProductVariant()],
  paymentTerm: PAYMENT_TERMS.Prepayment,
  billingInterval: BILLING_INTERVALS.Monthly,
  proRata: false,
  isCalendarAligned: false,
});

export const getDefaultCreditMeteredPromotion = (): PlanInputFormUsageProduct => ({
  id: newId(),
  variants: [getDefaultCreditMeteredProductVariant()],
  paymentTerm: PAYMENT_TERMS.Postpayment,
  billingInterval: BILLING_INTERVALS.Monthly,
  proRata: true,
  isCalendarAligned: true,
});

export function getDefaultPercentagePromotion(): PlanInputFormFixedProduct {
  return {
    id: newId(),
    variants: [getDefaultPercentageDeductionProductVariant()],
    paymentTerm: PAYMENT_TERMS.Prepayment,
    billingInterval: BILLING_INTERVALS.Monthly,
    proRata: false,
    isCalendarAligned: false,
  };
}

export const getDefaultTiersUsageProductVariant = (): UsageProductVariantInput => ({
  id: newId(),
  pricing: {
    pricingModel: PRICING_MODELS.Tiers,
    priceConfiguration: {
      tiers: [{ from: 0, to: DEFAULT_INFINITY_VALUE, price: 0, flatRate: 0 }],
    },
  },
});

export const getDefaultRevShareProductVariant = (): UsageProductVariantInput => ({
  id: newId(),
  pricing: {
    pricingModel: PRICING_MODELS.Tiers,
    priceConfiguration: {
      isRevShare: true,
      isVolume: true,
      tiers: [{ from: 0, to: DEFAULT_INFINITY_VALUE, price: 0, flatRate: 0 }],
    },
  },
});

export const getDefaultTieredSubscriptionUsageProductVariant = (
  tierLock = true,
): UsageProductVariantInput => ({
  id: newId(),
  pricing: {
    pricingModel: PRICING_MODELS.TieredSubscription,
    priceConfiguration: {
      tiers: [{ from: 0, to: DEFAULT_INFINITY_VALUE, price: 0, flatRate: 0 }],
      tierLock,
    },
  },
});

export const getDefaultVolumeUsageProductVariant = (): UsageProductVariantInput => ({
  id: newId(),
  pricing: {
    pricingModel: PRICING_MODELS.Tiers,
    priceConfiguration: {
      isVolume: true,
      tierLock: false,
      tiers: [{ from: 0, to: DEFAULT_INFINITY_VALUE, price: 0, flatRate: 0 }],
    },
  },
});

export const getDefaultPerUnitUsageProductVariant = (): UsageProductVariantInput => ({
  id: newId(),
  pricing: {
    pricingModel: PRICING_MODELS.PerUnit,
    priceConfiguration: {},
  },
});

export const getDefaultCreditMeteredProductVariant = (): UsageProductVariantInput => ({
  id: newId(),
  pricing: {
    pricingModel: PRICING_MODELS.PerUnit,
    priceConfiguration: {
      price: -1,
      isMeteredCredit: true,
    },
  },
});

export const getDefaultFixedDeductionProductVariant = (): FixedProductVariantInput => ({
  id: newId(),
  pricing: {
    pricingModel: PRICING_MODELS.FixedDeduction,
  },
});

export const getDefaultPercentageDeductionProductVariant = (): FixedProductVariantInput => ({
  id: newId(),
  pricing: {
    pricingModel: PRICING_MODELS.PercentageDeduction,
  },
});

export const getDefaultFixedProductVariant = (): FixedProductVariantInput => ({
  id: newId(),
  settings: {
    productType: FIXED_PRODUCT_TYPES.Service,
    productConfiguration: {},
  },
  pricing: {
    pricingModel: PRICING_MODELS.FixedOneTime,
  },
});

export const getDefaultRefundProductVariant = (): FixedProductVariantInput => ({
  id: newId(),
  pricing: {
    pricingModel: PRICING_MODELS.Refund,
  },
});

export const getDefaultCommissionProductVariant = (): FixedProductVariantInput => ({
  id: newId(),
  pricing: {
    pricingModel: PRICING_MODELS.Commission,
  },
});

export const getDefaultSubscriptionProductVariant = (): FixedProductVariantInput => ({
  id: newId(),
  pricing: {
    pricingModel: PRICING_MODELS.Subscription,
    priceConfiguration: {
      price: 0,
      upfrontPay: false,
      shouldCreateSeparateInvoice: false,
    },
  },
});

export const getDefaultCommercialTermsProductVariant = (): CommercialTermsProductVariantInput => ({
  id: newId(),
  pricing: {
    pricingModel: PRICING_MODELS.CommercialTerms,
    priceConfiguration: {
      shouldCreateSeparateInvoice: true,
    },
  },
});
